// Canonical public site URL used in every @id / url field of emitted JSON-LD.
// Must be the real indexable hostname (npilookup.com), NOT the API backend URL
// (REACT_APP_BACKEND_URL) which would mis-attribute pages to the wrong host.
const SITE_URL = "https://npilookup.com";

/**
 * BreadcrumbList schema — used on every page.
 * @param {Array<{name: string, url?: string}>} items
 */
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    ...(item.url ? { "item": `${SITE_URL}${item.url}` } : {}),
  })),
});

/**
 * MedicalWebPage schema for specialty directory pages.
 */
export const medicalWebPageSchema = (specialtyName, description, url) => ({
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  "name": `${specialtyName} Providers`,
  "description": description,
  "url": `${SITE_URL}${url}`,
  "specialty": {
    "@type": "MedicalSpecialty",
    "name": specialtyName,
  },
  "about": {
    "@type": "MedicalSpecialty",
    "name": specialtyName,
  },
});

/**
 * Build a rich Physician/MedicalBusiness entry for ItemList use.
 * Google's healthcare rich results favor Physician with:
 * address, medicalSpecialty, identifier (NPI), areaServed, url.
 * @param {object} p - provider object with number (NPI), basic, taxonomies, addresses
 */
export const providerSchemaItem = (p, position) => {
  const basic = p.basic || {};
  const isOrg = p.enumeration_type === "NPI-2" || basic.organization_name;
  const taxonomies = p.taxonomies || [];
  const primary = taxonomies.find(t => t.primary) || taxonomies[0] || {};
  const addrs = p.addresses || [];
  const loc = addrs.find(a => a.address_purpose === "LOCATION") || addrs[0] || {};
  const name = isOrg
    ? basic.organization_name
    : [basic.first_name, basic.last_name].filter(Boolean).join(" ") +
      (basic.credential ? `, ${basic.credential}` : "");

  const entry = {
    "@type": "ListItem",
    "position": position,
    "item": {
      "@type": isOrg ? "MedicalBusiness" : "Physician",
      "@id": `${SITE_URL}/provider/${p.number}#provider`,
      "name": name || "Healthcare Provider",
      "url": `${SITE_URL}/provider/${p.number}`,
      "identifier": {
        "@type": "PropertyValue",
        "propertyID": "NPI",
        "value": p.number,
      },
    },
  };

  if (primary.desc) {
    entry.item.medicalSpecialty = {
      "@type": "MedicalSpecialty",
      "name": primary.desc,
    };
  }
  if (loc.address_1) {
    entry.item.address = {
      "@type": "PostalAddress",
      "streetAddress": loc.address_1,
      ...(loc.address_2 ? { "streetAddress": `${loc.address_1}, ${loc.address_2}` } : {}),
      "addressLocality": loc.city || "",
      "addressRegion": loc.state || "",
      "postalCode": (loc.postal_code || "").slice(0, 5),
      "addressCountry": "US",
    };
  }
  if (loc.telephone_number) {
    entry.item.telephone = loc.telephone_number;
  }
  if (loc.state) {
    entry.item.areaServed = {
      "@type": "State",
      "name": loc.state,
    };
  }
  return entry;
};

/**
 * Rich ItemList schema built from raw provider records. Outputs Physician /
 * MedicalBusiness entries with address, medicalSpecialty, and NPI identifier —
 * eligible for Google's healthcare provider rich results.
 *
 * @param {string} name — list headline ("Cardiologists in Texas")
 * @param {string} description — list description
 * @param {Array} providers — raw NPPES provider objects (max 10 surfaced)
 * @param {string} url — canonical URL for this list
 */
export const richItemListSchema = (name, description, providers, url) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": name,
  "description": description,
  "url": `${SITE_URL}${url}`,
  "numberOfItems": providers.length,
  "itemListElement": providers.slice(0, 10).map((p, i) => providerSchemaItem(p, i + 1)),
});

/**
 * ItemList schema for state directory pages — top 10 providers.
 */
export const itemListSchema = (stateName, providers) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": `Top Healthcare Providers in ${stateName}`,
  "numberOfItems": providers.length,
  "itemListElement": providers.slice(0, 10).map((p, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": p.name || "Healthcare Provider",
    "url": `${SITE_URL}/provider/${p.npi}`,
  })),
});

/**
 * WebApplication schema for /find-a-doctor.
 */
export const webApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Find a Doctor - NPI Lookup",
  "url": `${SITE_URL}/find-a-doctor`,
  "applicationCategory": "HealthApplication",
  "operatingSystem": "Web",
  "description":
    "Search for doctors using everyday terms. Our tool maps common medical needs to official CMS specialties to help you find the right provider.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
  },
});

/**
 * WebSite + SearchAction schema for homepage.
 */
export const webSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "NPI Lookup",
  "url": SITE_URL,
  "description":
    "Search over 6.2 million registered US healthcare providers. Verify NPI numbers, find doctors by specialty, and access provider credentials instantly.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/**
 * Dataset schema for stats/data pages (bulk screening, OIG, PECOS).
 */
export const datasetSchema = (name, description, url) => ({
  "@context": "https://schema.org",
  "@type": "Dataset",
  "name": name,
  "description": description,
  "url": `${SITE_URL}${url}`,
  "license": "https://www.usa.gov/government-works",
  "creator": {
    "@type": "Organization",
    "name": "Centers for Medicare & Medicaid Services (CMS)",
  },
});

/**
 * Generic WebApplication schema for tool pages (verify, bulk screening, etc.).
 * Use when a page is primarily a tool, not content.
 */
export const softwareAppSchema = ({
  name,
  description,
  url,
  category = "HealthApplication",
  priceUsd = "0",
} = {}) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name,
  description,
  url: url ? (url.startsWith("http") ? url : `${SITE_URL}${url}`) : SITE_URL,
  applicationCategory: category,
  operatingSystem: "Web",
  offers: {
    "@type": "Offer",
    price: priceUsd,
    priceCurrency: "USD",
  },
  publisher: {
    "@type": "Organization",
    name: "NPI Lookup",
    url: SITE_URL,
  },
});

/**
 * Generic FAQPage schema — pass [{q, a}, ...]. Keeps every page that emits FAQs
 * consistent.
 */
export const faqPageSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q || f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a || f.answer,
    },
  })),
});

/**
 * SaaS subscription Offer schema for pricing tier cards. Output an array of
 * these inside an `OfferCatalog` to feed Google Merchant / rich results.
 */
export const productOfferSchema = ({ name, description, url, priceUsd, interval = "month", currency = "USD" }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  url: url ? (url.startsWith("http") ? url : `${SITE_URL}${url}`) : SITE_URL,
  brand: { "@type": "Brand", name: "NPI Lookup" },
  offers: {
    "@type": "Offer",
    url: url ? (url.startsWith("http") ? url : `${SITE_URL}${url}`) : SITE_URL,
    priceCurrency: currency,
    price: String(priceUsd),
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: String(priceUsd),
      priceCurrency: currency,
      unitCode: interval === "year" ? "ANN" : "MON",
    },
    availability: "https://schema.org/InStock",
  },
});
